import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Subscription, combineLatest, filter } from 'rxjs';
import moment from 'moment-timezone';
import * as am4core from '@amcharts/amcharts4/core';

import { PositiveNegativeRiverChartModel } from '@Common/Charts/models/PositiveNegativeRiverChart/positive-negative-river-chart.model';
import { ChartExportEventModel } from '@Common/Charts/models/chart-export-event.model';
import { ChartModel } from '@Common/Charts/models/chart.model';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { DateAndValueXYChart } from '@Common/Charts/models/date-and-value-x-y.chart';
import { MultiSeriesDateAndValueXYChart } from '@Common/Charts/models/multi-series-date-and-value-x-y.chart';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { StackedRiverChartModel } from '@Common/Charts/models/StackedRiverChart/stacked-river-chart.model';
import { ReportsChartDetailService } from '@Common/Reports/services/reports-chart-detail.service';

import { AlertsSurgeChartService } from '@Monitor/Alerts/services/alerts-surge-chart.service';
import { AlertHistoryModel } from '@Monitor/Alerts/models/alert-history.model';
import { MonitoringRoutingService } from '@Monitor/Core/services/monitoring-routing.service';

import { AlertsService } from '../../services/alerts.service';
import { AlertModel } from '../../models/alert.model';
import { AlertSurgeMetricEnum } from '@Monitor/Alerts/enums/alert-surge-metric.enum';


@Component({
    selector: 'agpr-alert-dashboard',
    templateUrl: './alert-dashboard.component.html',
    styleUrls: ['./alert-dashboard.component.scss'],
    standalone: false
})
export class AlertDashboardComponent implements OnInit, OnDestroy {
    readonly messageSeverities = messageSeverityEnum;
    readonly alertSurgeMetrics = AlertSurgeMetricEnum;

    alert: AlertModel;
    alertHistory: AlertHistoryModel;

    chart: PositiveNegativeRiverChartModel | DateAndValueXYChart | MultiSeriesDateAndValueXYChart;
    sentimentChart: StackedRiverChartModel;

    loading: boolean = true;
    sourceChanged: boolean = false;
    startTargetPeriod: string;
    endTargetPeriod: string;

    subscriptions: Subscription[] = [];

    @ViewChild('trendChartContainer') trendChartContainer: ElementRef;

    constructor(
        private alertsService: AlertsService,
        private alertsSurgeChartService: AlertsSurgeChartService,
        private currentUserStateService: CurrentUserStateService,
        private reportsChartDetailService: ReportsChartDetailService,
        private routingService: MonitoringRoutingService,
    ) {}

    @HostListener('click', ['$event.target']) onClick(event) {
        if (event?.classList?.contains('viewMentionLink')) {
            this.reportsChartDetailService.viewCoverageAnalysisDialog(
                event.href,
                null,
                this.trendChartContainer.nativeElement.contains(event) ?
                    new MultiSeriesDateAndValueXYChart(this.chart.rawSeries.map(series => series.clone()), this.chart.options, this.chart.metadata)
                    // eslint-disable-next-line max-len
                    : new StackedRiverChartModel(this.sentimentChart.rawSeries.map(series => series.clone()), this.sentimentChart.options, this.sentimentChart.metadata)
            );
            return false;
        }
    }

    ngOnInit() {
        this.loading = true;
        this.subscriptions.push(
            combineLatest([
                this.alertsService.getAlertHistory().pipe(filter(x => !!x)),
                this.currentUserStateService.getPreferences()
            ]).subscribe(([alertHistory, preferences]) => {
                this.alertHistory = new AlertHistoryModel(alertHistory);
                this.alertsSurgeChartService.setAlertHistory(alertHistory);
                if (this.alertHistory && this.alert) {
                    this.alertsSurgeChartService.getSearches(alertHistory.impactfulDataChangeSinceSent);
                }
                this.endTargetPeriod = alertHistory ? moment(alertHistory.submittedDate).tz(preferences.timeZone).format('YYYY-MM-DD HH:mm') : '';
                this.startTargetPeriod = moment(this.endTargetPeriod).subtract(1, 'hour').format('YYYY-MM-DD HH:mm');
            }),
            this.alertsService.getCurrentAlert().subscribe(alert => {
                this.alert = alert;
                if (this.alertHistory && this.alert) {
                    this.alertsSurgeChartService.getSearches(this.alertHistory.impactfulDataChangeSinceSent);
                }
            }),
            this.routingService.getCurrentRoute().subscribe(
                route => {
                    // we need this for when we switch between triggered events, they sometimes load the old data
                    this.loading = true;
                    this.alertsService.setCurrentAlert(null);
                    this.alertsService.clearAlertHistory();
                    this.alertsService.setAlertSourceChanged(false);
                }
            ),
            this.alertsService.getAlertSourceChanged().subscribe(sourceChanged => {
                this.sourceChanged = sourceChanged;
            }),
            this.alertsSurgeChartService.getChart().subscribe(chart => {
                this.chart = chart;
                if (this.alertHistory && this.alert) {
                    this.loading = false;
                }
            }),
            this.alertsSurgeChartService.getSentimentChart().subscribe(sentimentChart => {
                this.sentimentChart = sentimentChart;
                if (this.alertHistory && this.alert) {
                    this.loading = false;
                }
            })
        );
    }

    public chartExport(event: ChartExportEventModel, chart: ChartModel) {
        chart.export(event.eventKey.toLowerCase() as keyof am4core.IExportOptions, chart.metadata.titleText);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
